import React, {useEffect, useState} from 'react';
import {Box, Button, ExpandableSection, Alert, Container, Header} from '@amzn/awsui-components-react';
import {MultiAllocationWorksheetData} from 'src/models/multidimensional-allocation/MultiAllocationWorksheetData';
import ServiceCollection from 'src/services/ServiceCollection';
import 'src/assets/styles/react-table.scss';
import {
    MultidimensionalAllocationContext
} from "src/services/multidimensional-allocation/MultidimensionalAllocationContext";
import {TPELoadingSpinner} from "src/components/shared/TPELoadingSpinner";
import {
    MultidimensionalAllocationDataSourceContext
} from "src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesContext";
import MultidimensionalAllocationDataSourceGrid
    from "src/components/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourceGrid";
import ArrayUtils from "src/utils/arrayUtils";
import {
    MULTI_ALLOCATION_DATA_SOURCE_ACTIONS,
    multidimensionalAllocationDataSourcesReducer
} from "src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesReducer";
import {ACTIONS} from "src/services/multidimensional-allocation/MultidimensionalAllocationReducer";
import useReducerWithLogger from "src/services/utils/ReducerWithLogger";
import {
    initialState
} from "src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesState";
import SimpleUseEffect from "src/components/shared/SimpleUseEffect";
import MultidimensionalAllocationDataSourceSplitGrid
    from "src/components/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourceSplitGrid";
import MultidimensionalAllocationDataSourceSelectCOA
    from "src/components/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourceSelectCOA";
import MultidimensionalAllocationDataSourcesMagicBox
    from "src/components/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesMagicBox";
import MultidimensionalAllocationResultsTable
    from "src/components/multidimensional-allocation/data-sources/MultidimensionalAllocationResultsTable";
import {ExecutionStatus} from "src/models/multidimensional-allocation/ExecutionStatus";
import CONSTANTS from "src/utils/constants";
import { ApprovalType } from 'src/models/multidimensional-allocation/ProcessApprovals';
import { ProcessApprovalModal } from '../shared/ProcessApprovalModal';
import { useApprovalButtonProps } from '../hooks/useApprovalButtonProps';
import { WorksheetStatus } from 'src/models/multidimensional-allocation/MultiAllocationSheetData';
import { WorksheetExecutionGroupDetails } from 'src/models/multidimensional-allocation/WorksheetExecutionGroupDetails';
import {
    GetMultiAllocationWorksheetExecutionStatusResponse
} from 'src/models/multidimensional-allocation/MultiAllocationWorksheetExecution';


const MultidimensionalAllocationDataSourceProvider = (props: any) => {
    const {state, dispatch, services, children, multidimensionalAllocationState} = props;
    const providerValue = React.useMemo(() => ({
        multidimensionalAllocationDataSourcesState: state,
        multidimensionalAllocationDataSourceDispatch: dispatch,
        services,
        multidimensionalAllocationState
    }), [state, dispatch, services]);
    return (
        <MultidimensionalAllocationDataSourceContext.Provider value={providerValue}>
            {children}
        </MultidimensionalAllocationDataSourceContext.Provider>
    );
}

interface MultidimensionalAllocationDataSourcesContainerProps {
    containerTitle?: string,
    expanded?: boolean,
    worksheet?: MultiAllocationWorksheetData,
    services: ServiceCollection,
    executionGroupDetails?: WorksheetExecutionGroupDetails,
    executionStatusResponse?: GetMultiAllocationWorksheetExecutionStatusResponse | null,
}

/**
 * Container component for managing multidimensional allocation data sources
 * Displays a configurable table using TPEReactTable for data source configuration
 */
export default function MultidimensionalAllocationDataSourcesContainer(props: MultidimensionalAllocationDataSourcesContainerProps) {
    const {containerTitle, expanded, executionGroupDetails, executionStatusResponse} = props;
    const {
        services,
        multidimensionalAllocationState,
        multidimensionalAllocationDispatch
    } = React.useContext(MultidimensionalAllocationContext)
    // Set up local data sources state
    const [dataSourcesState, dataSourcesDispatch] = useReducerWithLogger(multidimensionalAllocationDataSourcesReducer, initialState);
    const {
        datasetRecords,
        recordBeingEdited,
        customCoaReference = new Map(),
        recordBeingEditedOriginal,
        selectedCustomCoaValues
    } = dataSourcesState;
    const {sevenSegmentLOVMap, dataSourcesNeedRefresh, viewMode, worksheetExecution} = multidimensionalAllocationState;

    // Determine which worksheet version to use: historical or current
    const historicalWorksheetVersion = worksheetExecution?.worksheetVersion;
    const isViewingHistoricalData = historicalWorksheetVersion != null;
    const worksheetVersionToUse = historicalWorksheetVersion ?? multidimensionalAllocationState.selectedWorksheet?.worksheetVersion;

    const [loadTime, setLoadTime] = React.useState('');
    const [recordNeedsSaving, setRecordNeedsSaving] = React.useState(undefined as string | undefined);
    const [pullDataSourceBalance, setPullDataSourceBalance] = React.useState(undefined as string | undefined);
    const [tableKey, setTableKey] = React.useState(0);
    const [refreshRecordsFlag, setRefreshRecordsFlag] = useState(undefined as string | undefined);
    const [refreshBalanceStatusFlag, setRefreshBalanceStatusFlag] = useState(undefined as string | undefined);
    const [currentRefreshExecutionId, setCurrentRefreshExecutionId] = useState(undefined as string | undefined);
    const {showDataKeySelection = false} = recordBeingEdited || {};
    const [atLeastOneDatasetItemIsSaved, setAtLeastOneDatasetItemIsSaved] = useState(false);
    const [loadingBalanceStatuses, setLoadingBalanceStatuses] = useState(false);
    const [modalMode, setModalMode] = useState<'closed' | 'submit' | 'approval'>('closed');

    const [worksheetDataSourcesResult, customCoaReferenceMap, worksheetDataSourcesLoading, worksheetDataSourcesError] =
        services.multidimensionalAllocationService.getMultiAllocationWorksheetDataSources(
            multidimensionalAllocationState.selectedWorksheet?.worksheetId,
            worksheetVersionToUse,
            refreshRecordsFlag || loadTime
        )

    const [dataSourceTypes, currencyCodes, periods, currencyConversionTypes, loadingCheck, error] =
        services.dataSourcesService.getDataSourceLOVs(loadTime);

    const [coaSegmentsResponse, coaSegmentsLoading, coaSegmentError] =
        services.tpAllocationService.getAllCoaSegments(multidimensionalAllocationState.selectedWorksheet?.worksheetId);

    const [coaTypes, loadingCoa, errorCoa] = services.dataSourcesService.getCoaTypes(loadTime);

    const [allocationSheetsResponse, allocationSheetsLoading, allocationSheetsError] =
        services.multidimensionalAllocationService.listMultiAllocationSheets(loadTime, CONSTANTS.STATUS.ACTIVE);

    const [saveDataSourceResponse, saveDataSourceLoading, saveDataSourceError] =
        services.multidimensionalAllocationService.saveMultiAllocationWorksheetDataSource(false, recordBeingEdited, customCoaReferenceMap ?? undefined, recordNeedsSaving);

    const [datasourceExecutionId, executeMultiAllocationWorksheetDataSourceLoading, executeMultiAllocationWorksheetDataSourceError] =
        services.multidimensionalAllocationService.executeMultiAllocationWorksheetDataSource(recordBeingEdited?.worksheetId,
            recordBeingEdited?.worksheetVersion, recordBeingEdited?.datasourceId, pullDataSourceBalance)

    const [datasourceExecutionStatusResponse, datasourceExecutionStatusLoading, datasourceExecutionStatusError] = 
        services.multidimensionalAllocationService.getMultiAllocationExecutionStatus(
            multidimensionalAllocationState.selectedWorksheet?.worksheetId,
            currentRefreshExecutionId,
            refreshBalanceStatusFlag
        );

    useEffect(() => {
        setLoadTime(new Date().toDateString());
        setTableKey(tableKey + 1);
    }, []);

    useEffect(() => {
        setAtLeastOneDatasetItemIsSaved(datasetRecords.some(x => !x.isNewRecord))
    }, [datasetRecords])

    useEffect(() => {
        if (saveDataSourceError) {
            services.messageService.showErrorBanner(saveDataSourceError);
            return;
        }

        if (saveDataSourceResponse == null) {
            return;
        }

        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SAVE_RECORD.withPayload({
            recordID: recordBeingEdited?.datasourceId,
            updatedRecord: saveDataSourceResponse.dataSource
        }));

        // Clear the recordNeedsSaving flag after processing the save response
        setRecordNeedsSaving(undefined);
    }, [saveDataSourceResponse, saveDataSourceError])

    useEffect(() => {
        if (dataSourcesNeedRefresh) {
            setRefreshRecordsFlag(new Date().toISOString());
        }
    }, [dataSourcesNeedRefresh])

    // Handle pull balance execution response - update lastBalancePullId locally
    useEffect(() => {
        if (executeMultiAllocationWorksheetDataSourceError) {
            services.messageService.showErrorBanner(executeMultiAllocationWorksheetDataSourceError);
            return;
        }

        if (datasourceExecutionId && recordBeingEdited?.datasourceId) {
            dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_LAST_BALANCE_PULL_ID.withPayload({
                datasourceId: recordBeingEdited.datasourceId,
                lastBalancePullId: datasourceExecutionId
            }));
            
            setCurrentRefreshExecutionId(datasourceExecutionId);
            
            // Reset pullDataSourceBalance to prevent re-triggering execute API
            setPullDataSourceBalance(undefined);
        }
    }, [datasourceExecutionId, executeMultiAllocationWorksheetDataSourceError]);

    // Handle data source execution status response - refresh when completed
    useEffect(() => {
        if (datasourceExecutionStatusError) {
            services.messageService.showErrorBanner(datasourceExecutionStatusError);
            return;
        }

        if (datasourceExecutionStatusResponse?.datasourceStatuses &&
            datasourceExecutionStatusResponse.datasourceStatuses.length > 0 &&
            refreshBalanceStatusFlag) {
          
            // Datasource execution status response will only have one datasource 
            // TODO: Backend API to return execution status in response directly from backend
            const firstStatus = datasourceExecutionStatusResponse.datasourceStatuses[0];
            if (firstStatus) {
                if (firstStatus.executionError) {
                    services.messageService.showErrorBanner(firstStatus.executionError);
                    return;
                }

                const statusMap = new Map<string, string>();
                statusMap.set(firstStatus.datasourceId, firstStatus.executionStatus);
                dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_BALANCE_PULL_STATUS.withPayload(statusMap));
            }
            
            // Refresh data sources when the first status is completed
            if (firstStatus?.executionStatus === ExecutionStatus.COMPLETED) {
                multidimensionalAllocationDispatch(ACTIONS.SET_DATA_SOURCES_NEED_REFRESH.withPayload(true));
            }
        }
    }, [datasourceExecutionStatusResponse, datasourceExecutionStatusError, refreshBalanceStatusFlag, multidimensionalAllocationDispatch]);

    // Handle refresh balance status action
    useEffect(() => {
        if (dataSourcesState.refreshBalanceStatusDatasourceId) {
            const targetRecord = datasetRecords.find(r => r.datasourceId === dataSourcesState.refreshBalanceStatusDatasourceId);
            if (targetRecord?.lastBalancePullId) {
                setCurrentRefreshExecutionId(targetRecord.lastBalancePullId);
                setRefreshBalanceStatusFlag(new Date().toISOString());
            }
            // Always clear the refresh trigger regardless of whether we have lastBalancePullId
            dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.REFRESH_BALANCE_STATUS.withPayload(undefined));
        }
    }, [dataSourcesState.refreshBalanceStatusDatasourceId]);

    useEffect(() => {
        if (worksheetDataSourcesError) {
            services.messageService.showErrorBanner(worksheetDataSourcesError);
            return;
        }

        if (worksheetDataSourcesResult == null || worksheetDataSourcesLoading) {
            return;
        }

        if (worksheetDataSourcesResult.length == 0 && !worksheetDataSourcesLoading) {
            addNewRecord();
            return;
        }

        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_RECORDS.withPayload(worksheetDataSourcesResult))

        const fetchStatuses = async () => {
            if (worksheetDataSourcesResult.length > 0 && executionStatusResponse) {
                setLoadingBalanceStatuses(true);
                
                try {
                    const statusMap = new Map<string, string>();
                    // Use datasourceStatuses from the execution response
                    if (executionStatusResponse.datasourceStatuses && executionStatusResponse.datasourceStatuses.length > 0) {
                        executionStatusResponse.datasourceStatuses.forEach((dsStatus: any) => {
                            statusMap.set(dsStatus.datasourceId, dsStatus.executionStatus || "Not Started");
                        });
                    } else {
                        // Fallback to overall execution status if no datasource-specific statuses
                        worksheetDataSourcesResult.forEach(record => {
                            statusMap.set(record.datasourceId, executionStatusResponse?.executionStatus || "Not Started");
                        });
                    }

                    if (statusMap.size > 0) {
                        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_BALANCE_PULL_STATUS.withPayload(statusMap));
                    }
                } catch (error) {
                    console.error("Failed to fetch data source status:", error);
                }
                
                setLoadingBalanceStatuses(false);
            }
        };

        fetchStatuses();
        
    }, [worksheetDataSourcesResult, worksheetDataSourcesLoading, worksheetDataSourcesError]);

    useEffect(() => {
        if (datasetRecords == null) {
            return;
        }

        multidimensionalAllocationDispatch(ACTIONS.SET_DATASET_RECORDS.withPayload(datasetRecords))
    }, [datasetRecords]);

    useEffect(() => {
        if (customCoaReferenceMap == null) {
            return;
        }

        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_CUSTOM_COA_REFERENCE.withPayload(customCoaReferenceMap))
    }, [customCoaReferenceMap, dataSourcesDispatch]);

    useEffect(() => {
        if (error) {
            services.messageService.showErrorBanner(error);
            return;
        }

        if (ArrayUtils.isNullOrEmpty(dataSourceTypes) || ArrayUtils.isNullOrEmpty(currencyCodes)
            || ArrayUtils.isNullOrEmpty(periods) || ArrayUtils.isNullOrEmpty(currencyConversionTypes)) {
            return;
        }

        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_DATASOURCE_LOV.withPayload({
            dataSourceTypes, currencyCodes, periods, currencyConversionTypes
        }))
    }, [dataSourceTypes, error, currencyCodes, periods, currencyConversionTypes, dataSourcesDispatch]);

    useEffect(() => {
        if (coaSegmentError) {
            services.messageService.showErrorBanner(coaSegmentError);
            return;
        }

        if (coaSegmentsResponse == null) {
            return;
        }

        multidimensionalAllocationDispatch(ACTIONS.SET_SEVEN_SEGMENTS_LOV_MAP.withPayload(coaSegmentsResponse))
    }, [coaSegmentsResponse, coaSegmentError, multidimensionalAllocationDispatch]);

    useEffect(() => {
        if (allocationSheetsLoading) {
            dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_ALLOCATION_SHEETS_LOADING.withPayload(true));
        }
    }, [allocationSheetsLoading, dataSourcesDispatch]);

    useEffect(() => {
        if (allocationSheetsError) {
            services.messageService.showErrorBanner(allocationSheetsError);
            return;
        }

        if (allocationSheetsResponse == null) {
            return;
        }

        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_ALLOCATION_SHEETS.withPayload(allocationSheetsResponse.sheets || []));
    }, [allocationSheetsResponse, allocationSheetsError, dataSourcesDispatch]);

    const addNewRecord = function () {
        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.ADD_NEW_RECORD
            .withPayload({
                worksheetId: multidimensionalAllocationState.selectedWorksheet?.worksheetId,
                worksheetVersion: multidimensionalAllocationState.selectedWorksheet?.worksheetVersion,
                worksheetName: multidimensionalAllocationState.selectedWorksheet?.worksheetName
            }));
    }

    // Handle success callback for approval modal
    const handleApprovalSuccess = () => {
        // Refresh the data or navigate as needed
        window.location.reload();
    };

    // Compute validation message for the Submit for Review button
    const getValidationMessage = (): string | undefined => {
        // Check if there are any errors in data sources
        const hasDataSourceErrors = datasetRecords.some(record => 
            record.errors != null && record.errors.size > 0
        );
        if (hasDataSourceErrors) {
            return 'Cannot submit worksheet with data source errors';
        }

        // Check if there are no saved data sources
        const hasSavedDataSources = datasetRecords.some(record => !record.isNewRecord);
        if (!hasSavedDataSources) {
            return 'Must add at least one data source before submitting';
        }

        // Check if worksheet is not executed (balance not pulled for all saved data sources)
        const savedDataSources = datasetRecords.filter(record => !record.isNewRecord);
        const allDataSourcesExecuted = savedDataSources.every(record => 
            record.lastBalancePullId != null && record.lastBalancePullId !== ''
        );
        if (!allDataSourcesExecuted) {
            return 'Must execute worksheet (pull balance) for all data sources before submitting';
        }

        // Check if group is not assigned to the worksheet
        if (executionGroupDetails?.executionGroup == null) {
            return 'Must assign worksheet to an execution group before submitting';
        }

        return undefined;
    };

    // Get button properties using the reusable hook
    const approvalButtonProps = useApprovalButtonProps(
        (multidimensionalAllocationState.selectedWorksheet?.status as WorksheetStatus) || 'DRAFT_IN_PROGRESS',
        () => setModalMode('submit'),
        () => setModalMode('approval'),
        undefined, // Worksheets don't have validation data like sheets do
        getValidationMessage
    );

    return (
        <Container 
                data-class="polarisExpandableSection"
                header={<Header variant='h3'> { containerTitle } </Header>}
                footer={ <div className="actionButtonsContainer">
                            <Box float="left">
                                <Button
                                    variant={"normal"}
                                    onClick={addNewRecord}
                                    disabled={recordBeingEdited?.isEditing || isViewingHistoricalData || (viewMode != null && viewMode !== CONSTANTS.VIEW_MODE.EDITABLE)}
                                    disabledReason={
                                        isViewingHistoricalData
                                            ? "Cannot add data sources when viewing historical data"
                                            : (viewMode != null && viewMode !== CONSTANTS.VIEW_MODE.EDITABLE ? "Click Edit to make changes." : undefined)
                                    }
                                >
                                    Add data source
                                </Button>
                            </Box>
                            
                            {multidimensionalAllocationState.selectedWorksheet?.worksheetId && (
                                <Box float="right">
                                    <Button
                                        variant="primary"
                                        onClick={approvalButtonProps.onClick}
                                        disabled={approvalButtonProps.disabled || recordBeingEdited?.isEditing || isViewingHistoricalData || viewMode === CONSTANTS.VIEW_MODE.EDITABLE}
                                        disabledReason={
                                            isViewingHistoricalData
                                                ? "Cannot submit historical data for approval"
                                                : (viewMode === CONSTANTS.VIEW_MODE.EDITABLE ? "Confirm or revert your changes before submitting." : approvalButtonProps.disabledReason)
                                        }
                                    >
                                        {approvalButtonProps.label}
                                    </Button>
                                </Box>
                            )}
                        </div>
                    }
        >
           
            <MultidimensionalAllocationDataSourceProvider
                services={services}
                state={dataSourcesState}
                dispatch={dataSourcesDispatch}
                multidimensionalAllocationState={multidimensionalAllocationState}
            >
                <div className="wizardBoxContentContainer">
                    {isViewingHistoricalData && (
                        <Alert
                            type="info"
                            header="Viewing Historical Data"
                            dismissible={false}
                        >
                            You are viewing execution data from {multidimensionalAllocationState.selectedExecutionPeriod}.
                            Data is read-only in this view. Clear the period selection to return to current data.
                        </Alert>
                    )}
                    <div className="datasourceHeader">
                        <TPELoadingSpinner loading={worksheetDataSourcesLoading || loadingBalanceStatuses} loadingText={"Loading data sources"}>
                            <MultidimensionalAllocationDataSourceGrid key={1}/>
                        </TPELoadingSpinner>
                    </div>
                    {showDataKeySelection &&
                        <div className="dataKeyContainer">
                            <MultidimensionalAllocationDataSourceSelectCOA {...{
                                allRecordDescriptions: datasetRecords.map(x => x.description),
                                coaTypes,
                                customCoaReference,
                                dispatch: dataSourcesDispatch,
                                isSaving: saveDataSourceLoading,
                                onRecordNeedsSaving: (x: string) => setRecordNeedsSaving(x),
                                originalRecordDescription: recordBeingEditedOriginal?.description || '',
                                recordBeingEdited: {
                                    ...recordBeingEdited,
                                    entityId: recordBeingEdited?.datasourceId || ''
                                },
                                services,
                                sevenSegmentLOV: sevenSegmentLOVMap,
                                showDrillDownSelect: false,
                                saveButtonLabel: "Save data source"
                            }}
                            />
                            <MultidimensionalAllocationDataSourcesMagicBox
                                key={recordBeingEdited?.datasourceId}
                                recordBeingEdited={recordBeingEdited}
                                services={services}
                                dispatch={dataSourcesDispatch}
                                ACTIONS={MULTI_ALLOCATION_DATA_SOURCE_ACTIONS}
                                coaTypes={coaTypes}
                                selectedCustomCoaValues={selectedCustomCoaValues}
                                sevenSegmentLOV={sevenSegmentLOVMap}
                                setPullDataSourceBalance={setPullDataSourceBalance}
                                filteredAllocationSheets={dataSourcesState.filteredAllocationSheets}
                                allocationSheetsLoading={dataSourcesState.allocationSheetsLoading}
                                pullBalanceLoading={executeMultiAllocationWorksheetDataSourceLoading}
                                isViewingHistoricalData={isViewingHistoricalData}
                            />
                        </div>
                    }
                    {(showDataKeySelection && recordBeingEdited?.s3PresignUrl) &&
                        <div>
                            <MultidimensionalAllocationResultsTable preSignedUrl={recordBeingEdited?.s3PresignUrl}/>
                        </div>
                    }
                    {recordBeingEdited != undefined && datasetRecords.indexOf(recordBeingEdited) != datasetRecords.length - 1 &&
                        <div>
                            <MultidimensionalAllocationDataSourceSplitGrid key={tableKey}/>
                        </div>
                    }
                    <SimpleUseEffect
                        useEffectVars={[recordNeedsSaving]}
                        action={() => {
                            // Only trigger refresh after a successful save, not on toggle actions
                            if (saveDataSourceResponse && recordNeedsSaving) {
                                multidimensionalAllocationDispatch(ACTIONS.CHECK_AND_REFRESH_ENTITY_DETAILS);
                            }
                        }}
                    />
                    
                    {/* Process Approval Modal */}
                    {multidimensionalAllocationState.selectedWorksheet?.worksheetId && (
                        <ProcessApprovalModal
                            entityId={multidimensionalAllocationState.selectedWorksheet.worksheetId}
                            approvalType={ApprovalType.MULTI_ALLOCATION_WORKSHEET}
                            validationData={undefined} // Worksheets don't have validation data like sheets do
                            mode={modalMode}
                            onModeChange={setModalMode}
                            onSuccess={handleApprovalSuccess}
                        />
                    )}
                </div>
            </MultidimensionalAllocationDataSourceProvider>
        </Container>
    )
}
